'use client';

import React from 'react';
import { Box, Text, Title, Paper, Divider, List } from '@mantine/core';
import { useTranslations } from 'next-intl';

interface ConfirmationStepProps {
  resultsSummary: string;
}

const ConfirmationStep: React.FC<ConfirmationStepProps> = ({ resultsSummary }) => {
  const t = useTranslations('confirmation');
  const summaryLines = resultsSummary.trim().split('\n');

  return (
    <Box p="lg">
      <Paper shadow="xs" radius="md" p="md">
        <Title order={2} mb="md">
          {t('confirmYourPlan')}
        </Title>

        <Text size="lg" mb="md">
          {t('reviewDetails')}
        </Text>
        <Divider my="md" />
        <Title order={3} mb="sm">
          {t('summaryTitle')}
        </Title>
        {summaryLines.length > 0 ? (
          <List spacing="md" size="sm">
            {summaryLines.map((line, index) => {
              const [key, ...valueParts] = line.split(':');
              const value = valueParts.join(':').trim();
              let displayKey = key.trim();
              let displayValue = value;

              // Customize display based on the key
              switch (displayKey) {
                case 'Health Goals':
                  displayKey = t('healthGoals');
                  break;
                case 'Disease Types':
                  displayKey = t('diseaseTypes');
                  break;
                case 'Package':
                  displayKey = t('selectedPackage');
                  break;
                case 'Location':
                  displayKey = t('location');
                  break;
                case 'Payment Method':
                  displayKey = t('paymentMethod');
                  break;
                case 'Insurance Details':
                  if (!value || value === t('notProvided')) {
                    return null; // Don't display if not provided
                  }
                  displayKey = t('insuranceDetails');
                  break;
                case 'Lab Test':
                  displayKey = t('labTest');
                  displayValue = value === t('uploaded') ? t('yes') : t('no');
                  break;
                case 'Medical Report':
                  displayKey = t('medicalReport');
                  displayValue = value === t('uploaded') ? t('yes') : t('no');
                  break;
              }

              return (
                <List.Item key={index}>
                  <Text component="span">{displayKey}:</Text>
                  <Text component="span" ml={5}>
                    {displayValue}
                  </Text>
                </List.Item>
              );
            })}
          </List>
        ) : (
          <Text>{t('noSummary')}</Text>
        )}
      </Paper>
    </Box>
  );
};

export default ConfirmationStep;
